/**
 * useQuantXSignalR - SignalR Hook for Real-time QuantX Updates
 *
 * Connects to the QuantXHub on DataFeed Server for real-time trading analytics.
 * Requires JWT authentication via query string parameter.
 *
 * Events:
 * - TrendUpdate: H4/D1 bars, trend score changes
 * - FibLevelAlert: Price near Fibonacci level
 * - AdxRegimeChange: Volatility regime change
 * - SessionChange: Trading session change
 *
 * @see docs/HANDOFF_QuantX_Integration.md
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { dataFeedURL } from '../config';

// Hub URL
const QUANTX_HUB_URL = `${dataFeedURL}/hubs/quantx`;

// Reconnection delays (in ms): immediate, 2s, 5s, 10s, 30s
const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000];

/**
 * useQuantXSignalR Hook
 *
 * @param {Object} options - Hook configuration options
 * @param {string} options.token - JWT authentication token (required)
 * @param {string} options.symbol - Trading symbol to subscribe to (e.g., 'XAUUSD_ct')
 * @param {boolean} options.enabled - Enable/disable connection (default: true)
 * @param {Function} options.onTrendUpdate - Callback for TrendUpdate events
 * @param {Function} options.onFibLevelAlert - Callback for FibLevelAlert events
 * @param {Function} options.onAdxRegimeChange - Callback for AdxRegimeChange events
 * @param {Function} options.onSessionChange - Callback for SessionChange events
 * @param {Function} options.onError - Callback for connection errors
 *
 * @returns {Object} Hook state and actions
 */
const useQuantXSignalR = ({
    token,
    symbol,
    enabled = true,
    onTrendUpdate,
    onFibLevelAlert,
    onAdxRegimeChange,
    onSessionChange,
    onError
}) => {
    // State
    const [connectionState, setConnectionState] = useState('disconnected');
    const [isConnected, setIsConnected] = useState(false);
    const [lastEvent, setLastEvent] = useState(null);
    const [subscribedSymbol, setSubscribedSymbol] = useState(null);
    const [error, setError] = useState(null);

    // Refs
    const connectionRef = useRef(null);
    const isMountedRef = useRef(true);

    /**
     * Create SignalR connection with JWT authentication
     */
    const createConnection = useCallback(() => {
        if (!token) {
            console.warn('[QuantXSignalR] No token provided - cannot connect');
            return null;
        }

        // Build URL with JWT token in query string
        // WebSocket connections cannot use Authorization header
        const hubUrl = `${QUANTX_HUB_URL}?access_token=${encodeURIComponent(token)}`;

        console.log('[QuantXSignalR] Creating connection to:', QUANTX_HUB_URL);

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl)
            .withAutomaticReconnect(RECONNECT_DELAYS)
            .configureLogging(signalR.LogLevel.Information)
            .build();

        return connection;
    }, [token]);

    /**
     * Subscribe to symbol updates
     */
    const subscribe = useCallback(async (symbolToSubscribe) => {
        if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
            console.warn('[QuantXSignalR] Cannot subscribe - not connected');
            return false;
        }

        try {
            console.log('[QuantXSignalR] Subscribing to:', symbolToSubscribe);
            await connectionRef.current.invoke('SubscribeToQuantX', symbolToSubscribe);
            setSubscribedSymbol(symbolToSubscribe);
            console.log('[QuantXSignalR] Subscribed successfully to:', symbolToSubscribe);
            return true;
        } catch (err) {
            console.error('[QuantXSignalR] Subscribe failed:', err);
            setError(err);
            onError?.(err);
            return false;
        }
    }, [onError]);

    /**
     * Unsubscribe from symbol updates
     */
    const unsubscribe = useCallback(async (symbolToUnsubscribe) => {
        if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
            return false;
        }

        try {
            console.log('[QuantXSignalR] Unsubscribing from:', symbolToUnsubscribe);
            await connectionRef.current.invoke('UnsubscribeFromQuantX', symbolToUnsubscribe);
            setSubscribedSymbol(null);
            return true;
        } catch (err) {
            console.error('[QuantXSignalR] Unsubscribe failed:', err);
            return false;
        }
    }, []);

    /**
     * Start connection and set up event handlers
     */
    const connect = useCallback(async () => {
        if (connectionRef.current) {
            // Already have a connection
            if (connectionRef.current.state === signalR.HubConnectionState.Connected) {
                return true;
            }
        }

        const connection = createConnection();
        if (!connection) return false;

        connectionRef.current = connection;

        // Connection state change handlers
        connection.onreconnecting((error) => {
            console.log('[QuantXSignalR] Reconnecting...', error);
            setConnectionState('reconnecting');
            setIsConnected(false);
        });

        connection.onreconnected((connectionId) => {
            console.log('[QuantXSignalR] Reconnected:', connectionId);
            setConnectionState('connected');
            setIsConnected(true);
            // Re-subscribe after reconnection
            if (symbol) {
                subscribe(symbol);
            }
        });

        connection.onclose((error) => {
            console.log('[QuantXSignalR] Connection closed', error);
            setConnectionState('disconnected');
            setIsConnected(false);
            if (error) {
                setError(error);
                onError?.(error);
            }
        });

        // Event handlers
        connection.on('TrendUpdate', (data) => {
            console.log('[QuantXSignalR] TrendUpdate:', data);
            setLastEvent({ type: 'TrendUpdate', data, timestamp: new Date() });
            onTrendUpdate?.(data);
        });

        connection.on('FibLevelAlert', (data) => {
            console.log('[QuantXSignalR] FibLevelAlert:', data);
            setLastEvent({ type: 'FibLevelAlert', data, timestamp: new Date() });
            onFibLevelAlert?.(data);
        });

        connection.on('AdxRegimeChange', (data) => {
            console.log('[QuantXSignalR] AdxRegimeChange:', data);
            setLastEvent({ type: 'AdxRegimeChange', data, timestamp: new Date() });
            onAdxRegimeChange?.(data);
        });

        connection.on('SessionChange', (data) => {
            console.log('[QuantXSignalR] SessionChange:', data);
            setLastEvent({ type: 'SessionChange', data, timestamp: new Date() });
            onSessionChange?.(data);
        });

        // Start connection
        try {
            setConnectionState('connecting');
            await connection.start();
            console.log('[QuantXSignalR] Connected successfully');
            setConnectionState('connected');
            setIsConnected(true);
            setError(null);

            // Subscribe to symbol if provided
            if (symbol) {
                await subscribe(symbol);
            }

            return true;
        } catch (err) {
            console.error('[QuantXSignalR] Connection failed:', err);
            setConnectionState('error');
            setIsConnected(false);
            setError(err);
            onError?.(err);
            return false;
        }
    }, [createConnection, symbol, subscribe, onTrendUpdate, onFibLevelAlert, onAdxRegimeChange, onSessionChange, onError]);

    /**
     * Disconnect from hub
     */
    const disconnect = useCallback(async () => {
        if (connectionRef.current) {
            try {
                await connectionRef.current.stop();
                console.log('[QuantXSignalR] Disconnected');
            } catch (err) {
                console.error('[QuantXSignalR] Disconnect error:', err);
            }
            connectionRef.current = null;
        }
        setConnectionState('disconnected');
        setIsConnected(false);
        setSubscribedSymbol(null);
    }, []);

    /**
     * Reconnect to hub
     */
    const reconnect = useCallback(async () => {
        await disconnect();
        await connect();
    }, [disconnect, connect]);

    // Connect on mount if enabled
    useEffect(() => {
        isMountedRef.current = true;

        if (enabled && token) {
            connect();
        }

        return () => {
            isMountedRef.current = false;
            disconnect();
        };
    }, [enabled, token, connect, disconnect]);

    // Handle symbol changes
    useEffect(() => {
        if (isConnected && symbol) {
            // Unsubscribe from old symbol if different
            if (subscribedSymbol && subscribedSymbol !== symbol) {
                unsubscribe(subscribedSymbol).then(() => {
                    subscribe(symbol);
                });
            } else if (!subscribedSymbol) {
                subscribe(symbol);
            }
        }
    }, [symbol, isConnected, subscribedSymbol, subscribe, unsubscribe]);

    return {
        // State
        connectionState,
        isConnected,
        isConnecting: connectionState === 'connecting',
        isReconnecting: connectionState === 'reconnecting',
        subscribedSymbol,
        lastEvent,
        error,

        // Actions
        connect,
        disconnect,
        reconnect,
        subscribe,
        unsubscribe
    };
};

export default useQuantXSignalR;
